import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';

type BeaconParams = Record<string, string | number | undefined>;
type ExcludedPlacementProduct = {
  productKey: string;
  statusCode: number;
};

type ProductBeaconParams = {
  quantity?: number;
  slot?: number;
};

const toKey = (value?: string | number | null): string => String(value ?? '').trim();

const toBeaconUrl = (beaconUrl?: string): string | undefined => {
  const trimmedBeaconUrl = beaconUrl?.trim();

  if (!trimmedBeaconUrl || typeof window === 'undefined') {
    return undefined;
  }

  if (trimmedBeaconUrl.startsWith('//')) {
    return `${window.location.protocol}${trimmedBeaconUrl}`;
  }

  return new URL(trimmedBeaconUrl, window.location.origin).toString();
};

const appendBeaconParams = (beaconUrl: string, params?: BeaconParams): string => {
  if (!params) {
    return beaconUrl;
  }

  const url = new URL(beaconUrl);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      url.searchParams.set(key, `${value}`);
    }
  });

  return url.toString();
};

export const sendCriteoBeacon = (beaconUrl?: string, params?: BeaconParams): void => {
  const finalUrl = beaconUrl ? appendBeaconParams(beaconUrl, params) : undefined;

  if (!finalUrl || typeof window === 'undefined') {
    return;
  }

  const sendFetchBeacon = () =>
    fetch(finalUrl, {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'omit',
      keepalive: true,
      cache: 'no-store',
    });

  try {
    const isBeaconSent = typeof navigator.sendBeacon === 'function' ? navigator.sendBeacon(finalUrl) : false;

    if (!isBeaconSent) {
      void sendFetchBeacon();
    }
  } catch {
    void sendFetchBeacon();
  }
};

export const getCriteoPlacementId = (product?: CriteoSponsoredProduct): string | undefined => product?.placement?.id;

export const getCriteoProductKey = (product?: CriteoSponsoredProduct): string | undefined => {
  const primaryKey = [product?.ParentSKU, product?.ProductId].map(toKey).find(Boolean);
  return primaryKey || undefined;
};

export const isCriteoSponsoredHit = (
  hit?: AlgoliaProductHit
): hit is AlgoliaProductHit & { criteoProduct: CriteoSponsoredProduct } => Boolean(hit?.criteoProduct);

export const sendCriteoProductClickBeacon = (product?: CriteoSponsoredProduct, params?: ProductBeaconParams): void => {
  sendCriteoBeacon(toBeaconUrl(product?.OnClickBeacon), params);
};

export const sendCriteoProductViewBeacon = (product?: CriteoSponsoredProduct, params?: ProductBeaconParams): void => {
  sendCriteoBeacon(toBeaconUrl(product?.OnViewBeacon), params);
};

export const sendCriteoBasketChangeBeacon = (
  product?: CriteoSponsoredProduct,
  quantity = 1,
  params?: ProductBeaconParams
): void => {
  sendCriteoBeacon(toBeaconUrl(product?.OnBasketChangeBeacon), { ...params, quantity });
};

export const sendCriteoWishlistBeacon = (product?: CriteoSponsoredProduct, params?: ProductBeaconParams): void => {
  sendCriteoBeacon(toBeaconUrl(product?.OnWishlistBeacon), params);
};

export const sendCriteoPlacementLoadBeacon = ({
  product,
  excludedProducts = [],
}: {
  product?: CriteoSponsoredProduct;
  excludedProducts?: Array<ExcludedPlacementProduct>;
}): void => {
  const placementLoadBeaconUrl = toBeaconUrl(product?.placement?.OnLoadBeacon);

  sendCriteoBeacon(placementLoadBeaconUrl, {
    ex: excludedProducts.length ? excludedProducts.map(({ productKey }) => productKey).join('|') : undefined,
    st: excludedProducts.length ? excludedProducts.map(({ statusCode }) => `${statusCode}`).join('|') : undefined,
  });
};

export const sendCriteoPlacementViewBeacon = (product?: CriteoSponsoredProduct): void => {
  sendCriteoBeacon(toBeaconUrl(product?.placement?.OnViewBeacon));
};
